import { EspectaculoGrilla, Fecha, Funcion, FuncionConDate, TipoGenerico } from './models/types';
import { FechasRequest, FechaString, ISelectOptions } from './models/interfaces';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { AxiosError } from 'axios';


// Limpia los campos para el formulario de usuario en caso de registro, ya que es dinámico y se usa el mismo para edición
export const limpiarDefaultValues = <T extends { defaultValue?: unknown }>(lista: T[]): T[] => {
  return lista.map(el => ({
    ...el,
    defaultValue: '',
  }));
};

/* MANEJO DE FECHAS */

export function obtenerFechasTaller(fechas: FechasRequest): Fecha[] {
  // Nota: esta función es O(rango_de_días * horarios). Optimizar solo si se vuelve un hot-path (medir primero).
  if (!fechas || !fechas.FechaInicio || !fechas.FechaFin || !fechas.Horarios || fechas.Horarios.length === 0) {
    throw new Error('Parámetros de entrada no válidos');
  }

  const fechaInicio = new Date(fechas.FechaInicio);
  const fechaFin = new Date(fechas.FechaFin);
  fechaFin.setDate(fechaFin.getDate() + 1); // Agregamos un día porque el último día no lo toma

  const fechasCreadas: Fecha[] = [];

  const diaSemanaIndex: Record<number, number> = {
    // Emparejamos los index de JS con el Enum de DiasSemana, ya que JS toma Domingo como 0
    0: 7,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
  };

  let d = new Date(fechaInicio);
  while (d <= fechaFin) {
    const diaSemanaJS = d.getDay();
    const diaSemanaEnum = diaSemanaIndex[diaSemanaJS];

    const horariosDia = fechas.Horarios.filter(h => h.Dia === diaSemanaEnum); // Buscamos TODOS los horarios correspondientes al día actual

    horariosDia.forEach(horarioDia => {
      const horaIniPartes = horarioDia.HoraIni.split(':');
      const horaFinPartes = horarioDia.HoraFin.split(':');

      const fechaInicioConHora = new Date(d);
      fechaInicioConHora.setHours(parseInt(horaIniPartes[0]), parseInt(horaIniPartes[1]), 0, 0);

      const fechaFinConHora = new Date(d);
      fechaFinConHora.setHours(parseInt(horaFinPartes[0]), parseInt(horaFinPartes[1]), 0, 0);

      fechasCreadas.push({
        Id: 0,
        FechaInicio: fechaInicioConHora,
        FechaFin: fechaFinConHora,
      });
    });

    d.setDate(d.getDate() + 1);
  }

  return fechasCreadas;
}

// A partir de un array par de pares de fechas de tipo Date, obtiene un array de pares de fechas en tipo string
export function convertirFechasAFormatoString(fechas: Fecha[]): FechaString[] {
  const fechasStringResultante: FechaString[] = [];

  for (const fecha of fechas) {
    const fechaString: FechaString = {
      Id: 0,
      // Convertimos la fecha local a ISO 8601 sin la modificación de la zona horaria
      FechaInicio: convertirAISOConHoraLocal(fecha.FechaInicio),
      FechaFin: convertirAISOConHoraLocal(fecha.FechaFin),
    };

    fechasStringResultante.push(fechaString);
  }

  return fechasStringResultante;
}

// A partir de un array par de pares de fechas de tipo string, obtiene un array de pares de fechas en tipo Date
export function convertirFechasStringAFechas(fechaString: FechaString): Fecha {
  const fechaInicio = parseISO(fechaString.FechaInicio);
  const fechaFin = parseISO(fechaString.FechaFin);

  if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) {
    throw new Error('Error al convertir fecha: formato de fecha inválido');
  }

  return {
    Id: fechaString.Id,
    FechaInicio: fechaInicio,
    FechaFin: fechaFin,
  };
}

export function convertirFechasAHorarios(fechas: Fecha[] | undefined): string[] {
  if (!fechas) return [];

  const horariosUnicos = new Set<string>();

  fechas.forEach(fecha => {
    let fechaInicio = new Date(fecha.FechaInicio);
    let fechaFin = new Date(fecha.FechaFin);
    // Día de la semana
    const diasSemana = ['Domingos', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábados'];
    const diaSemana = diasSemana[fechaInicio.getDay()];

    // Formatear horas
    const opcionesHora: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    const horaInicio = fechaInicio.toLocaleTimeString('es-ES', opcionesHora);
    const horaFin = fechaFin.toLocaleTimeString('es-ES', opcionesHora);

    const horarioString = `${diaSemana} de ${horaInicio} a ${horaFin} `;
    horariosUnicos.add(horarioString);
  });

  // Convertir el Set a array
  return Array.from(horariosUnicos);
}

// Función para convertir fecha a formato ISO 8601 manteniendo la hora local
function convertirAISOConHoraLocal(fecha: Date): string {
  const offset = fecha.getTimezoneOffset() * 60000;
  const fechaLocal = new Date(fecha.getTime() - offset);
  return fechaLocal.toISOString();
}

export const formatearDateAFechaHoraUY = (fecha: Date): string => {
  const fechaEnZona = toZonedTime(fecha, 'America/Montevideo');
  return format(fechaEnZona, 'dd/MM/yy HH:mm', { locale: es });
};

export const formatearDateAFechaDiaMesUY = (fecha: Date) =>
  format(toZonedTime(fecha, 'America/Montevideo'), 'd MMM', { locale: es });

export const formatearDateAHorasUY = (fecha: Date): string => {
  const fechaEnZona = toZonedTime(fecha, 'America/Montevideo');
  return format(fechaEnZona, 'HH:mm', { locale: es });
};

// Convierte par de fechas en string al formato ISO String para enviar por JSON
export function convertirFechasAISOString(fechas: string[]): string[] {
  return fechas.map(f => {
    const fecha = new Date(f);
    if (isNaN(fecha.getTime())) {
      throw new Error(`Fecha inválida: ${f}`);
    }
    return fecha.toISOString();
  });
}

export function agregarDuracionAFechas(fechas: string[], duracion: number): string[] {
  const duracionEnMilisegundos = duracion * 60 * 1000; // Convertir a milisegundos
  return fechas.map(fecha => {
    const fechaObjeto = new Date(fecha);
    fechaObjeto.setTime(fechaObjeto.getTime() + duracionEnMilisegundos);
    return fechaObjeto.toISOString();
  });
}

const obtenerMesEnEspanol = (mes: number): string => {
  const meses = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  return meses[mes];
};

const obtenerDiaMes = (dia: number): string => {
  if (dia < 10) {
    return `0${dia}`;
  } else {
    return dia?.toString();
  }
};

// Recibe una fecha en formato Date y devuelve una fecha en formato DD-MM-AAAA
export const formatoFechaYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses en JavaScript empiezan en 0
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Recibe una fecha en formato stringy devuelve una fecha en formato DD-MM-AAAA
export const formatearFechaParaListado = (fechaISO: string): string => {
  if (!fechaISO) {
    return 'Sin fechas';
  }

  const fecha = new Date(fechaISO);
  if (isNaN(fecha.getTime())) {
    return 'Fecha inválida';
  }

  const dia = fecha.getDate().toString().padStart(2, '0');
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const año = fecha.getFullYear();
  return `${dia}-${mes}-${año}`;
};

// Recibe una fecha en formato stringy devuelve una fecha en formato DD-MM-AAAA HH:MM
export const formatearFechaHoraParaListado = (fechaISO: string): string => {
  if (!fechaISO) {
    return 'Sin fechas';
  }

  const fecha = new Date(fechaISO);
  if (isNaN(fecha.getTime())) {
    return 'Fecha inválida';
  }

  const dia = fecha.getDate().toString().padStart(2, '0');
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const año = fecha.getFullYear();
  const horas = fecha.getHours().toString().padStart(2, '0');
  const minutos = fecha.getMinutes().toString().padStart(2, '0');

  return `${dia}-${mes}-${año} ${horas}:${minutos}`;
};

export const getDiaSemana = (fecha: Date): string => {
  if (!(fecha instanceof Date) || isNaN(fecha.getTime())) {
    console.error('Fecha inválida:', fecha);
    throw new Error('Fecha inválida');
  }
  const opciones = { weekday: 'long' } as const;
  const diaSemana = new Intl.DateTimeFormat('es-UY', opciones).format(fecha);
  return diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
};

// Recibe un par de fechas en formato Date y devuelve "Del Día de Mes al Día de Mes de Año"
export const formateoFechasParaCaratulas = (fechaIni: Date, fechaFin: Date): string => {
  const fecha1 = new Date(fechaIni);
  const fecha2 = new Date(fechaFin);

  const unSoloDia = fecha1?.getDate() === fecha2?.getDate();
  const mismoMes = fecha1?.getMonth() === fecha2?.getMonth();

  const diaMesFormateadoInicio = obtenerDiaMes(fecha1?.getDate());
  const año = `de ${fecha2?.getFullYear()}`;

  const fechaInicioFormateada = `${diaMesFormateadoInicio} de ${obtenerMesEnEspanol(fecha1?.getMonth())}`;
  const fechaFinFormateada = `${obtenerDiaMes(fecha2?.getDate())} de ${obtenerMesEnEspanol(fecha2?.getMonth())}`;
  return unSoloDia
    ? `${fechaInicioFormateada} ${año}`
    : mismoMes
      ? `Del ${diaMesFormateadoInicio} al ${fechaFinFormateada} ${año}`
      : `Del ${fechaInicioFormateada} al ${fechaFinFormateada} ${año}`;
};

// Formato de fecha hora para Ficha de Espectáculo
export const formatoFechaHora = (fecha: Date): string | null => {
  if (!fecha) return null;
  return `${getDiaSemana(fecha)} ${obtenerDiaMes(fecha.getDate())} de ${obtenerMesEnEspanol(fecha.getMonth())} - ${formatearDateAHorasUY(fecha)} hs.`;
};

// Ordena los espectáculos por fecha descendente en la grilla
export function ordenarPorFecha(espectaculos: EspectaculoGrilla[]): EspectaculoGrilla[] {
  return [...espectaculos].sort((a, b) => {
    const tieneFechaA = a.Fechas?.length > 0;
    const tieneFechaB = b.Fechas?.length > 0;

    // Si uno de los espectáculos no tiene fechas, lo movemos al final
    if (!tieneFechaA && tieneFechaB) return 1;
    if (tieneFechaA && !tieneFechaB) return -1;
    if (!tieneFechaA && !tieneFechaB) return 0;

    // Si ambos tienen fechas, los comparamos por la fecha de inicio más temprana
    const fechaInicioA = new Date(a.Fechas[0].FechaInicio);
    const fechaInicioB = new Date(b.Fechas[0].FechaInicio);

    return fechaInicioA.getTime() - fechaInicioB.getTime();
  });
}

// Valida si una función sigue vigente antes de mostrar sus fechas
export const validarFuncionVigente = (fechaFin: Date): boolean => {
  return new Date(fechaFin).getTime() >= Date.now();
};

export const formatearFuncionesConDate = (funciones: Funcion[]): FuncionConDate[] => {
  return funciones.map(funcion => ({
    ...funcion,
    Fecha: {
      FechaInicio: new Date(funcion.Fecha.FechaInicio),
      FechaFin: new Date(funcion.Fecha.FechaFin),
    },
  }));
};

/* OPCIONES PARA SELECTS */

export const crearOpcionesTipos = (tipos: TipoGenerico[], entidad: string): ISelectOptions[] => {
  return tipos?.map(tipo => ({
    entidad: entidad,
    nombre: tipo.Nombre,
    id: tipo.Id,
  })) ?? [];
};

export const crearOpcionesTalleresUsuario = (
  tipos: TipoGenerico[],
  entidad: string,
  idTalleresUsu: number[] = []
): ISelectOptions[] => {
  return tipos?.map(tipo => {
    const estaInscripto = idTalleresUsu.some(taller => taller === tipo.Id);

    return {
      entidad: entidad,
      nombre: tipo.Nombre,
      id: tipo.Id,
      disabled: estaInscripto,
    };
  }) ?? [];
};

export const crearOpcionesSelect = <T extends Record<string, unknown>>(
  objetoEnum: T,
  entidad: string
): ISelectOptions[] => {
  return Object.keys(objetoEnum)
    .filter(key => isNaN(Number(key)))
    .map(key => ({
      entidad,
      nombre: key !== '0' ? (key as string) : 'Seleccionar',
      id: objetoEnum[key] as number,
    }));
};

/* VARIAS */

export const obtenerTextoDesdeEnum = <T extends object>(objetoEnum: T, value: T[keyof T]): string => {
  const nombreEnum = (Object.keys(objetoEnum) as Array<keyof T>).find(key => objetoEnum[key] === value);
  if (nombreEnum === undefined) {
    throw new Error(`No se encontró el texto para el enum: ${value}`);
  }
  return nombreEnum.toString();
};

export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

export const decodificarToken = (token: string): JwtPayload => {
  const segments = token.split('.');
  if (segments.length !== 3 || !segments[1]) {
    throw new Error('Token JWT malformado');
  }
  const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload) as JwtPayload;
};

// Para manejo de errores
export const obtenerError = (error: AxiosError<string>, setError: (message: string) => void) => {
  let errorMessage = 'Error desconocido';

  if (error.response) {
    if (error.response.data && typeof error.response.data === 'string') {
      if (error.response.data.includes('System.')) {
        errorMessage = 'Error en el servidor';
      } else if (error.response.data.includes('Error enviando email: Forbidden')) {
        errorMessage =
          'Error: está caído el servicio de correos. Por favor, intentá más tarde o comunicate con un administrador.';
      } else if (error.response.data.includes('Could not find file')) {
        errorMessage = 'Hubo un problema en el servidor, pero la operación se realizó correctamente.';
      } else {
        errorMessage = error.response.data;
      }
    } else if (error.response.status === 401 || error.response.status === 403) {
      errorMessage = 'No tienes permisos para esta operación';
    } else if (error.response.status === 500) {
      errorMessage = 'Error en el servidor';
    }
  } else if (error.request) {
    errorMessage = 'No hubo respuesta del servidor';
  } else {
    errorMessage = 'Error en la solicitud';
  }

  setError(errorMessage);
  return Promise.reject(new Error(errorMessage));
};

// Guarda datos en el store con un tiempo de expiración
export const storeDatosConExpiracion = (key: string, value: string, ttl: number) => {
  const now = new Date();

  // Time To Live en milisegundos
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };

  localStorage.setItem(key, JSON.stringify(item));
};

// Obtiene los datos del store validando si ya pasó su tiempo de expiración
export const getCompraConExpiracion = (key: string) => {
  const compraJSON = localStorage.getItem(key);

  if (!compraJSON) {
    return null;
  }

  try {
    const item = JSON.parse(compraJSON);
    if (!item || typeof item.expiry !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    if (Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

// Limita el largo del texto (utilizado en el cuerpo del mail del Listado de Boletines)
export const limitarTexto = (texto: string, maxCaracteres: number): string => {
  if (texto.length > maxCaracteres) {
    return texto.slice(0, maxCaracteres - 3) + '...';
  }
  return texto;
};
